import React, { useState } from 'react';
import {
  IAuditoriaLancamentoDiaNaoUtilResultado,
  IItemLancamentoDiaNaoUtil,
} from '../interfaces/IAuditoriaLancamentoDiaNaoUtil';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaLancamentoDiaNaoUtilResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    case 'AVISO_TECNICO':
      return 'bi-calendar-check';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-215: rótulo legível — "AVISO_TECNICO" (nome interno, vocabulário de status) vira "Fechamento
// de Competência" na tela, para não parecer um erro de digitação nem um aviso operacional comum.
const getRotuloStatus = (status: string) => {
  switch (status) {
    case 'AVISO_TECNICO':
      return 'Fechamento de Competência';
    default:
      return status;
  }
};

const AuditoriaLancamentoDiaNaoUtilDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const lancamentosDiaNaoUtil = resultado.lancamentosDiaNaoUtil || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Lançamentos em Finais de Semana e Feriados — {getRotuloStatus(resultado.status)}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        {/* COR-214: KPI separado do "Impacto Fiscal Estimado" — antes só existia o card fiscal
            (sempre R$ 0,00, correto por design, mas facilmente confundido com "valor dos
            lançamentos" quando a mensagem principal também dizia "totalizando R$ 0,00"). */}
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Valor Total dos Lançamentos Identificados</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          <div className='auditoria-kpi-help'>Soma dos valores dos lançamentos em dia não útil — dá contexto de materialidade.</div>
        </div>

        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          <div className='auditoria-kpi-help'>Sem multa aplicável — este módulo é um aviso de conformidade temporal (CPC 00), não calcula multa/passivo.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / em Dia Não Útil</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-aviso'>{lancamentosDiaNaoUtil.length}</span>
          </div>
          <div className='auditoria-kpi-help'>Lançamentos em sábado, domingo, feriado nacional ou ponto facultativo no período.</div>
        </div>

        {resultado.totalAvisosTecnicos > 0 && (
          <div className='auditoria-kpi'>
            <div className='auditoria-kpi-label'>Fechamento de Competência</div>
            <div className='auditoria-kpi-value'>
              <span className='auditoria-contador auditoria-contador-secundario'>{resultado.totalAvisosTecnicos}</span>
            </div>
            <div className='auditoria-kpi-help'>
              Último dia do mês, histórico compatível com tributo/provisão/apuração — exceção contábil esperada,
              sem risco operacional aparente. Não contabilizado em "Avisos" operacionais.
            </div>
          </div>
        )}

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base contábil aplicada aos lançamentos em dia não útil.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Lançamentos em Finais de Semana e Feriados</h3>
        <p className='text-muted'>
          Lançamentos (Ativo, Passivo e Resultado) registrados em sábado, domingo, feriado nacional ou ponto
          facultativo — fora do expediente comercial, sujeitos a verificação de conformidade temporal. Não cobre
          feriados estaduais/municipais.
        </p>

        {lancamentosDiaNaoUtil.length === 0 && (
          <div className='text-muted mt-4'>Nenhum lançamento em final de semana ou feriado identificado no período.</div>
        )}

        {lancamentosDiaNaoUtil.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Nº Lançamento</th>
                  <th>Data</th>
                  <th>Tipo de Dia</th>
                  <th>Histórico</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosDiaNaoUtil.map((item: IItemLancamentoDiaNaoUtil, idx) => {
                  const chave = `ld-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-aviso' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>{item.numeroLancamento || '—'}</td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td>{item.tipoDiaNaoUtil}</td>
                        <td style={{ maxWidth: 240 }}>
                          {item.historico}
                          {/* COR-216: sinalização auxiliar de natureza provável — só informativa,
                              nunca muda o status nem exclui o lançamento da lista. */}
                          <div className='text-muted' style={{ fontSize: 11, fontStyle: 'italic' }}>
                            {item.naturezaProvavel
                              ? `Possível justificativa: ${item.naturezaProvavel}`
                              : 'Sem justificativa identificada'}
                          </div>
                        </td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{getRotuloStatus(item.status)}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: item.ehFechamentoCompetencia ? '#eff6ff' : '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div className={`auditoria-alert status-${item.status} mb-2`}>
                                <i className={`bi ${getIconeStatus(item.status)} auditoria-alert-icon`} />
                                <div>{item.mensagemObrigatoria}</div>
                              </div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                              <div className='mt-1'><strong>Natureza provável:</strong> {item.sinalizacaoNaturezaProvavel}</div>
                              <div><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaLancamentoDiaNaoUtilDetalhe;
